import React, { useState, useEffect, useRef, useCallback } from 'react';
import cards from '../../../styles/shared/ProductCards.module.css';
import styles from './AdminProductCard.module.css';
import noImageIcon from '../../../images/no-image-icon.svg';
import { API_URL } from '../../../api/client';
import EditProductModal from '../EditProductModal';
import ConfirmationModal from '../../ui/ConfirmationModal';
import Portal from '../../ui/Portal';
import { MdDelete, MdEdit } from 'react-icons/md';
import actions from '../../../styles/shared/RequestActions.module.css';

const AdminProductCard = React.memo(({ producto, isOpen, onOpen, onClose, deleteProducto }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const cardRef = useRef(null);
    const imageRef = useRef(null);

    useEffect(() => {
        if (producto.images && producto.images.length > 0) {
            setThumbnailUrl(`${API_URL}${producto.images[0].thumbnail}`);
            setImageUrl(`${API_URL}${producto.images[0].image}`);
        } else {
            setThumbnailUrl(null);
            setImageUrl(null);
        }
        setIsImageLoaded(false);
    }, [producto]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: '100px',
                threshold: 0.1
            }
        );

        const node = cardRef.current;
        if (node) {
            observer.observe(node);
        }

        return () => {
            if (node) {
                observer.unobserve(node);
            }
        };
    }, []);

    const handleImageLoad = useCallback(() => {
        setIsImageLoaded(true);
    }, []);

    useEffect(() => {
        const img = imageRef.current;

        if (isVisible && imageUrl && img) {
            if (img.complete) {
                handleImageLoad();
            } else {
                img.addEventListener('load', handleImageLoad);
            }

            return () => {
                if (img) {
                    img.removeEventListener('load', handleImageLoad);
                }
            };
        }
    }, [isVisible, imageUrl, handleImageLoad]);

    // Se confirma con el modal de siempre (ConfirmationModal) en vez de window.confirm
    const handleDelete = useCallback(async () => {
        setConfirmDeleteOpen(false);
        const success = await deleteProducto(producto.id);
        if (success) {
            window.location.reload();
        } else {
            console.error('Error deleting product');
            window.alert('No se pudo eliminar el producto. Inténtalo de nuevo.');
        }
    }, [producto.id, deleteProducto]);

    const handleEdit = useCallback((e) => {
        e.stopPropagation();
        onOpen();
    }, [onOpen]);

    const isOutOfStock = producto.cantidad === 0;
    const nombre = producto.equipo || 'Producto';
    return (
        <div className={`${cards.cardWrapper} ${styles.wrapper} ${isOutOfStock ? styles.outOfStock : ''}`} ref={cardRef}>
            <div className={`card ${cards.card} ${styles.card}`}>
                <div className={styles.imageContainer}>
                    {thumbnailUrl && (
                        <img 
                            src={thumbnailUrl} 
                            alt={nombre} 
                            className={`card-img-top ${cards.cardImgTop} ${styles.thumbnail}`}
                        />
                    )}
                    {imageUrl && (
                        <img 
                            ref={imageRef}
                            src={imageUrl} 
                            alt={nombre} 
                            className={`card-img-top ${cards.cardImgTop} ${styles.fullImage} ${isImageLoaded ? styles.loaded : ''}`}
                        />
                    )}
                    {!thumbnailUrl && !imageUrl && (
                        <img src={noImageIcon} alt="" className={`${styles.noImageIcon} card-img-top ${cards.cardImgTop}`} />
                    )}
                    {/* Solo sobre la imagen: los botones de editar y eliminar siguen visibles */}
                    {isOutOfStock && <span className={styles.soldOut}>Agotado</span>}
                </div>
                <div className={styles.body}>
                    <p className={styles.brand}>{producto.marca ? producto.marca : 'Marca no disponible'}</p>
                    <h5 className={styles.name} title={producto.equipo}>{producto.equipo}</h5>
                    <div className={styles.footer}>
                        <span className={styles.stock} title="Cantidad disponible">
                            <b>{producto.cantidad}</b> unid.
                        </span>
                        <div className={`${actions.group} ${styles.actions}`}>
                            <button
                                type="button"
                                className={`${actions.iconButton} ${styles.actionButton}`}
                                onClick={handleEdit}
                                aria-label={`Editar ${nombre}`}
                                title="Editar"
                            >
                                <MdEdit aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className={`${actions.iconButton} ${actions.danger} ${styles.actionButton}`}
                                onClick={() => setConfirmDeleteOpen(true)}
                                aria-label={`Eliminar ${nombre}`}
                                title="Eliminar"
                            >
                                <MdDelete aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {isOpen && (
                <EditProductModal
                    producto={producto}
                    onClose={onClose}
                />
            )}
            {confirmDeleteOpen && (
                <Portal>
                    <ConfirmationModal
                        isOpen
                        onClose={() => setConfirmDeleteOpen(false)}
                        onConfirm={handleDelete}
                        message={`¿Desea eliminar "${nombre}"?`}
                    />
                </Portal>
            )}
        </div>
    );
});

export default AdminProductCard; 